import { mapMutations } from 'vuex';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import EditMixin from '@/components/core/dtwin/editMixin';
import GeoDataMixin from '@/components/core/dtwin/geoDataMixin';
import deviceMixin from '@/components/is/assetMgmt/deviceMixin';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import mixinActionBoxStyles from '@/components/core/mixinActionBoxStyles';
export default {
  name: 'ViewISAssetMgmtDetails',
  mixins: [EditMixin, GeoDataMixin, deviceMixin, mixinActionBoxStyles],
  data: function () {
    return {
      twinSelectionTreeMenu: true,
      pageYOffset: 0,
      tabItem: this.$route.query.stateId ? 'tab-states' : this.$route.query.ruleId ? 'tab-rules' : 'tab-dashboard',
      shouldOpenStateDetail: !!this.$route.query.stateId
    };
  },
  computed: {
    headerBG() {
      if (get(this.twinObject, 'data.twinImageId')) {
        return `${location.origin}/api/v1/upload/${this.twinObject.data.twinImageId}`;
      }
      return './img/dtwin-factory1.png';
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map(status => {
        return {
          text: this.operationalStatus[status],
          value: status
        };
      });
    }
  },
  watch: {
    twinDataStates() {
      if (this.twinDataStates.length) {
        if (this.shouldOpenStateDetail && this.$refs['stateTable']) {
          this.finishedLoadingStatesTable();
        }
      }
      this.setupAssetDevice();
      this.twinObject = cloneDeep(this.twinObject);
    },
    $route() {
      // the component is not reloaded/re-mounted if the twin-id in the route changes
      // so perform the setup again for a different route; i.e. swap out the displayed details on-the-fly
      this.teardownDtwinSocket();
      this.setup();
      if (get(this.$route, 'query.stateId')) {
        this.tabItem = 'tab-states';
      } else if (get(this.$route, 'query.ruleId')) {
        this.tabItem = 'tab-rules';
      }
    },
    deviceData() {
      this.deviceData.forEach((device, i) => {
        this.fetchBatteryInformation(i);
      });
    }
  },
  mounted() {
    this.baseLink = 'is-assetmgmt/assets';
    window.addEventListener('scroll', this.saveScrollOffset);
    this.setup();
    this.setupAssetDevice();
  },
  destroyed() {
    window.removeEventListener('scroll', this.saveScrollOffset);
  },
  methods: {
    ...mapMutations('app', ['snackbar']),
    setupAssetDevice() {
      const deviceId =
      // prefer the hardwired device-id
      get(this.twinObject, 'data.assetManagement.deviceIds[0]') ||
      // otherwise get the first iothub-identifier
      get(this.twinDataStates.filter(state => get(state, 'properties.binding.type') === 'iothub'), '[0].properties.binding.options.identifier') || null;
      if (deviceId) {
        this.setupAssetTwin([deviceId]);
      }
    },
    digitalTwinChanged(selection) {
      if (parseInt(selection.id) !== parseInt(this.$route.params.id)) {
        this.$router.push(`/is-assetmgmt/assets/${selection.id}`);
      }
      this.twinSelectionTreeMenu = false;
    },
    breadCrumbsPath() {
      const ancestorList = this.ancestorList();
      if (ancestorList.length) {
        return ancestorList.map(a => {
          return {
            to: `/digitaltwin-mgmt/${a.id}`,
            text: a.title
          };
        });
      }
      return [{
        to: '/is-assetmgmt/',
        text: this.$i18n_t('IS.AssetMgmt.drawer.isAssetMgmtList')
      }];
    },
    saveScrollOffset() {
      this.pageYOffset = window.pageYOffset;
    },
    finishedLoadingStatesTable() {
      if (this.$route.query.stateId) {
        this.$nextTick(() => {
          const state = this.twinDataStates.find(s => s.id === parseInt(this.$route.query.stateId));
          if (state) {
            this.shouldOpenStateDetail = false;
            this.$refs['stateTable'].editState(state);
          }
        });
      }
    },
    didUpdateStates() {
      this.refreshStates();
    },
    dialogDisable() {
      this.dtwinToggleState();
    },
    prepareSubmit() {
      this.twinObject.data.assetManagement.deviceIds = this.deviceData.map(d => d.deviceId);
      this.handleSubmit();
    },
    fetchBatteryInformation(index) {
      if (this.deviceData[index].batteryLevel === undefined && get(this.deviceData[index], 'deviceType.properties.battery_level')) {
        NiotaClassicApiService.deviceFetchPackets(this.deviceData[index].deviceId, 0, 0).then(res => {
          if (get(res, 'data.packets[0].source_data')) {
            this.setLastBatteryLevel(get(res, 'data.packets[0].source_data'), index);
          }
        }).catch(() => {
          this.devicePackets = [];
        });
      }
    },
    setLastBatteryLevel(sourceData, index) {
      // clone, in order to avoid the original array being modified
      const devData = cloneDeep(this.deviceData);
      const batteryLevelVariable = get(devData, `[${index}]deviceType.properties.battery_level.variable`);
      if (sourceData.parsed && batteryLevelVariable) {
        devData[index].batteryLevel = sourceData.parsed[batteryLevelVariable];
        if (devData[index].batteryLevel === undefined) {
          // do not create endless fetch loops
          devData[index].batteryLevel = null;
        }
        this.deviceData = cloneDeep(devData);
      }
    },
    getBatteryLevel(deviceData) {
      let text = '';
      if (deviceData.batteryLevel || deviceData.batteryLevel === 0) {
        text = deviceData.batteryLevel;
      }
      const valueType = get(deviceData, 'deviceType.properties.battery_level.value_type_name');
      if (valueType) {
        text += valueType;
      }
      return text;
    }
  }
};